import * as THREE from 'three';
import type { GameMap, PileInfo } from '@sunderfen/shared';
import { itemModel } from './icon3d.js';
import { GEAR, isGearId } from '@sunderfen/shared';
import { G, addMesh, freeTree } from './models.js';
import type { Particles } from './particles.js';
import type { Glows } from './glows.js';

interface Tele { meshes: THREE.Mesh[]; until: number; start: number }
interface Shot { mesh: THREE.Object3D; from: THREE.Vector3; to: THREE.Vector3; t: number; dur: number; arc: number; done: () => void; trail?: number }

/**
 * Combat in the world: telegraph tiles that warn of a special, loot sacks and graves on the
 * ground, and arrows and frost bolts in flight.
 */
export class CombatFx {
  /** Piles are clickable: each carries userData.pile = id. */
  readonly piles = new Map<number, THREE.Group>();
  private teles: Tele[] = [];
  private shots: Shot[] = [];
  private tileGeo = new THREE.PlaneGeometry(0.92, 0.92).rotateX(-Math.PI / 2);
  private teleMat = new THREE.MeshBasicMaterial({ color: 0xe0281a, transparent: true, opacity: 0.45, depthWrite: false });

  /** A ring under whatever you're fighting. */
  private ring: THREE.Mesh;
  constructor(private scene: THREE.Scene, private map: GameMap, private particles: Particles, private glows: Glows) {
    const g = new THREE.RingGeometry(0.42, 0.52, 28); g.rotateX(-Math.PI / 2);
    this.ring = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xff5a3a, transparent: true, opacity: 0.8, depthWrite: false, toneMapped: false }));
    this.ring.renderOrder = 4; this.ring.visible = false;
    const ticks = new THREE.Group();
    for (let i = 0; i < 4; i++) { const t = new THREE.Mesh(new THREE.PlaneGeometry(0.1, 0.2).rotateX(-Math.PI / 2), this.ring.material); t.position.set(Math.sin(i * Math.PI / 2) * 0.62, 0, Math.cos(i * Math.PI / 2) * 0.62); t.rotation.y = i * Math.PI / 2; ticks.add(t); }
    this.ring.add(ticks);
    scene.add(this.ring);
  }
  /** Show the target ring at (x, z) sized for the creature, or hide it. */
  setRing(at: { x: number; z: number; size: number } | null, now: number) {
    this.ring.visible = !!at;
    if (!at) return;
    this.ring.position.set(at.x, Math.max(this.map.heightAt(at.x, at.z), 0) + 0.04, at.z);
    this.ring.scale.setScalar(at.size * (1 + Math.sin(now / 160) * 0.04));
    this.ring.rotation.y = now / 1400;
  }

  /** Mark tiles that a special will hit in `seconds`. */
  telegraph(tiles: [number, number][], seconds: number, now: number) {
    const meshes = tiles.map(([x, z]) => {
      const m = new THREE.Mesh(this.tileGeo, this.teleMat.clone());
      m.position.set(x + 0.5, Math.max(this.map.heightAt(x + 0.5, z + 0.5), 0) + 0.05, z + 0.5);
      m.renderOrder = 4;
      this.scene.add(m);
      return m;
    });
    this.teles.push({ meshes, start: now, until: now + seconds * 1000 + 250 });
  }

  /** Something flies from a to b; `done` runs when it lands. */
  shoot(from: THREE.Vector3, to: THREE.Vector3, kind: 'arrow' | 'bolt' | 'rock', done: () => void) {
    let mesh: THREE.Object3D;
    if (kind === 'arrow') { const g = new THREE.Group(); addMesh(g, G.box(0.03, 0.03, 0.6), 0xc8a070, 0, 0, 0); addMesh(g, G.box(0.05, 0.05, 0.08), 0xa3aab1, 0, 0, 0.32); addMesh(g, G.box(0.08, 0.01, 0.1), 0xf0f0e8, 0, 0, -0.26); mesh = g; }
    else if (kind === 'rock') mesh = new THREE.Mesh(G.dod(0.1), new THREE.MeshLambertMaterial({ color: 0x7a7468 }));
    else mesh = new THREE.Mesh(G.oct(0.14), new THREE.MeshBasicMaterial({ color: 0xbff4ff }));
    mesh.position.copy(from);
    this.scene.add(mesh);
    const dist = from.distanceTo(to);
    this.shots.push({ mesh, from: from.clone(), to: to.clone(), t: 0, dur: Math.max(0.18, dist / (kind === 'bolt' ? 12 : 18)), arc: kind === 'rock' ? dist * 0.2 : kind === 'arrow' ? dist * 0.06 : 0, done, trail: kind === 'bolt' ? 0 : undefined });
  }

  setPile(p: PileInfo) {
    this.removePile(p.id);
    const g = new THREE.Group();
    const y = Math.max(this.map.heightAt(p.x + 0.5, p.z + 0.5), 0.02);
    g.position.set(p.x + 0.5 + (p.kind === 'loot' ? 0.15 : 0), y, p.z + 0.5);
    if (p.kind === 'grave') {
      addMesh(g, G.box(0.5, 0.7, 0.14), 0x8a8a86, 0, 0.35, -0.2);
      addMesh(g, G.cyl(0.25, 0.25, 0.14, 8, ), 0x8a8a86, 0, 0.7, -0.2).rotation.x = Math.PI / 2;
      addMesh(g, G.box(0.06, 0.3, 0.02), 0x3a3a38, 0, 0.5, -0.12);
      addMesh(g, G.box(0.2, 0.06, 0.02), 0x3a3a38, 0, 0.58, -0.12);
      addMesh(g, G.box(0.6, 0.08, 0.8), 0x5a4a32, 0, 0.04, 0.15);
      g.userData.glow = this.glows.add(g.position.x, y + 0.5, g.position.z - 0.1, p.mine ? 0xffd27a : 0x9fb8ff, 1.2, { base: 0.25, night: 0.5, flicker: 0.2 });
    } else {
      const rare = p.items.some(i => i.rarity === 'rare' || i.rarity === 'very_rare' || i.rarity === 'shared');
      // the drops themselves, laid on the ground as they look in the pack: rarest first, up to six
      const RANK: Record<string, number> = { very_rare: 0, shared: 1, rare: 2, uncommon: 3, common: 4, always: 5 };
      const shown = [...p.items].sort((a, b) => (RANK[a.rarity] ?? 6) - (RANK[b.rarity] ?? 6)).slice(0, 6);
      g.position.x -= p.kind === 'loot' ? 0.15 : 0;
      // laid out side by side on the tile so each one can be told apart, not heaped
      const SPOTS: [number, number][] = [[0, 0], [-0.3, -0.2], [0.3, -0.18], [-0.28, 0.24], [0.3, 0.26], [0, -0.36]];
      shown.forEach((it, i) => {
        const m = itemModel(it.item);
        if (!m) { addMesh(g, G.dod(0.14), 0xa88a5a, SPOTS[i][0], 0.1, SPOTS[i][1]); return; }
        const holder = new THREE.Group();
        holder.position.set(SPOTS[i][0], 0, SPOTS[i][1]);
        holder.rotation.y = ((i * 137 + p.id * 29) % 360) * Math.PI / 180;
        // weapons and long things lie at their length; small things a little bigger than life so they read
        const long = isGearId(it.item) && GEAR[it.item].slot === 'weapon';
        m.scale.multiplyScalar(long ? 0.62 : i === 0 ? 0.36 : 0.3);
        m.rotation.x = -Math.PI / 2 + 0.3; // lying down, tipped a little toward the light
        m.updateMatrixWorld(true);
        const lowest = new THREE.Box3().setFromObject(m).min.y;
        m.position.y = -lowest + 0.01;
        holder.add(m); g.add(holder);
      });
      if (rare) g.userData.glow = this.glows.add(g.position.x, y + 0.3, g.position.z, p.items.some(i => i.rarity === 'very_rare') ? 0xd07aff : 0x6fb8ff, 1.0, { base: 0.5, night: 0.4, flicker: 0.25 });
    }
    g.traverse((o: THREE.Object3D) => { o.userData.pile = p.id; });
    g.userData.info = p;
    this.scene.add(g);
    this.piles.set(p.id, g);
  }
  removePile(id: number) {
    const g = this.piles.get(id);
    if (!g) return;
    this.scene.remove(g); freeTree(g);
    const glow = g.userData.glow as THREE.Sprite | undefined;
    if (glow) glow.visible = false;
    this.piles.delete(id);
  }
  pileInfo(id: number) { return this.piles.get(id)?.userData.info as PileInfo | undefined; }

  update(dt: number, now: number) {
    for (let i = this.teles.length - 1; i >= 0; i--) {
      const t = this.teles[i];
      if (now > t.until) { for (const m of t.meshes) { this.scene.remove(m); freeTree(m); } this.teles.splice(i, 1); continue; }
      const k = (now - t.start) / (t.until - t.start);
      for (const m of t.meshes) { (m.material as THREE.MeshBasicMaterial).opacity = (0.35 + 0.4 * k) * (0.7 + 0.3 * Math.sin(now / (90 - k * 60))); m.scale.setScalar(0.75 + 0.25 * k); }
    }
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i];
      s.t += dt / s.dur;
      if (s.t >= 1) { this.scene.remove(s.mesh); freeTree(s.mesh); this.shots.splice(i, 1); s.done(); continue; }
      const p = s.from.clone().lerp(s.to, s.t); p.y += Math.sin(s.t * Math.PI) * s.arc;
      s.mesh.lookAt(p.clone().add(s.to.clone().sub(s.from).normalize()));
      s.mesh.position.copy(p);
      if (s.trail !== undefined) this.particles.emit({ x: p.x, y: p.y, z: p.z, count: 1, spread: 0.05, vy: 0, vSpread: 0.2, colors: [0xbff4ff, 0xffffff], size: 0.08, life: 0.35, alpha: 0.8 }, true);
    }
  }
}
